<template>
  <div
    ref="basket"
    :class="[
      'z-500 w-full mx-auto bg-white border-t-2 border-t-brand-800 opacity-100',
      stickyMode ? 'fixed bottom-0 md:sticky' : 'fixed bottom-0'
    ]"
  >
    <slot></slot>
    <div class="z-500 w-full flex justify-center items-center min-h-14 md:h-16 pb-1 md:pb-0">
      <div class="w-full flex justify-center md:max-w-xl">
        <div class="w-1/3 md:w-auto p-2 md:p-1 flex justify-end">
          <button
            data-testid="basket-cart-button"
            class="transition-colors duration-300 ease-in-out relative p-3 h-full flex justify-end items-center rounded-md"
            :disabled="cartIsEmpty"
            :class="{
              'border-gray-brand-300 text-gray-brand-500 px-3 cursor-not-allowed opacity-60': cartIsEmpty,
              'bg-gradient-to-br from-brand-900 to-brand-800 md:hover:bg-brand-600 text-white px-5':
                !cartIsEmpty && !isActivelySubmittingCart && cartViewActive,
              'border-gray-brand-400 md:hover:bg-brand-100 md:hover:text-brand-600 text-brand-900 px-3':
                !cartIsEmpty && (isActivelySubmittingCart || !cartViewActive),
              'basket-cart-flash': cartFlash
            }"
            @click="clickedCart($event.target)"
            @animationend="cartFlash = false"
          >
            <div class="h-5 text-sm flex items-center justify-between space-x-1.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5 md:w-6 md:h-6"
                :class="{ invisible: isActivelySubmittingCart }"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
              <div class="tabular-nums" :class="{ invisible: isActivelySubmittingCart }">
                <div v-if="showPrice">
                  <div class="font-bold text-left">{{ displayCartTotal }}</div>
                  <!-- Two surfaces say "N items" since BOT-1108 added the top-bar cart pill, and
                       the nav hides on scroll - so anything asserting on the count needs to name
                       which one it means rather than taking the first match in the DOM. -->
                  <div data-testid="basket-count" class="font-normal text-xs text-left -mt-0.5">
                    {{ displayCartSize }} item{{ displayCartSize === 1 ? '' : 's' }}
                  </div>
                </div>
                <div v-if="!showPrice">
                  <div data-testid="basket-count" class="font-bold text-left">
                    {{ displayCartSize }} item{{ displayCartSize === 1 ? '' : 's' }}
                  </div>
                </div>
              </div>
            </div>
            <BasicSpinner
              v-if="isActivelySubmittingCart"
              class="absolute inset-0 flex items-center justify-center pointer-events-none"
            />
          </button>
        </div>
        <div
          class="w-2/3 md:w-1/2 p-2 md:p-1 pr-[18px] md:pr-1"
          :class="{ 'border-l-2 border-l-brand-800': !cartIsClear || cartIsEmpty }"
        >
          <PrimaryButton
            data-testid="basket-proceed"
            @click="clickedProceed"
            class="h-full w-full"
            :size="'lg'"
            :streaking="!cartIsEmpty && cartIsClear"
            :active="!cartIsEmpty && cartIsClear"
            :disabled="cartIsEmpty || !cartIsClear"
          >
            <span v-if="willAutocharge">Confirm order</span>
            <span v-else>Proceed to checkout</span>
            <template #right-icon><ArrowRightIcon class="h-4" /></template>
          </PrimaryButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
let strftime = require('strftime')
import BasicButton from '@/components/Molecules/BasicButton'
import BasicSpinner from '@/components/Molecules/BasicSpinner'
import { ArrowRightIcon } from '@heroicons/vue/solid'
import { mapGetters } from 'vuex'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import mapRelationship from '@/utils/mapRelationship'
import { posthog, getCommonTrackingProperties } from '@/composables/usePosthog'

// This bar has no fixed height: its default slot carries the pricing-tier progress and the cycle
// banner above the button row, so it ranges from ~56px to well over 150px, and it differs between
// V1 and V2 and between breakpoints. Anything floating above it therefore cannot be positioned with
// a constant - publishing the measured height lets a toast sit against what is actually on screen.
const BASKET_HEIGHT_VAR = '--merchant-basket-height'

// A route change mounts the incoming basket before the outgoing one unmounts, so the leaving
// instance must not clear a variable the arriving instance now owns.
let heightOwner = null

export default {
  components: { BasicButton, ArrowRightIcon, BasicSpinner, PrimaryButton },
  inject: {
    v2CommitCart: { default: null }
  },
  props: {
    inCart: {
      type: Boolean
    },
    inUpsell: {
      type: Boolean
    },
    stickyMode: {
      type: Boolean,
      default: false
    },
    // Whether the BOT-1262 cart drawer is currently open — the add-to-cart
    // flash only runs while it's closed, pointing the customer at where
    // their item went.
    drawerOpen: {
      type: Boolean,
      default: false
    },
    v2Cart: {
      type: Object,
      default: null
    },
    v2IsSubmitting: {
      type: Boolean,
      default: false
    },
    v2CartQuantity: {
      type: Number,
      default: 0
    },
    v2UpsellCategory: {
      type: Object,
      default: null
    },
    v2LocalCartItems: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters(['info']),
    link() {
      if (this.$store.getters.ownsBottle(this.$store.getters.currentBottle)) {
        return 'Checkout'
      } else if (this.$store.getters.authorizedForBottle(this.$store.getters.currentBottle)) {
        return 'Login'
      } else {
        return 'Home'
      }
    },
    isActivelySubmittingCart() {
      if (this.v2Cart) return this.v2IsSubmitting
      return this.$store.state.activelySubmittingCart
    },
    currentCart() {
      return this.$store.getters.currentCart
    },
    currentBottle() {
      return this.$store.getters.currentBottle
    },
    // The cart button's "active" styling means "you are looking at your cart" —
    // true for the legacy full-page cart view and for the open drawer alike.
    cartViewActive() {
      return this.inCart || this.drawerOpen
    },
    // CLEARING: COME BACK AND REFORM THESE
    cartIsClear() {
      if (this.v2Cart) return (this.v2Cart.cart_errors || []).length === 0
      return !this.currentBottle?.attributes?.validations?.has_cart_errors
    },
    showSeparator() {
      return false && !this.cartIsClear && !this.inCart
    },
    cartIsEmpty() {
      if (this.v2Cart) return this.v2CartQuantity === 0
      return this.$store.getters.cartSize == 0
    },
    showPrice() {
      if (this.v2Cart) return this.v2Cart.show_prices !== false
      return this.currentBottle?.attributes.show_prices
    },
    displayCartTotal() {
      if (this.v2Cart) return this.v2Cart.total_formatted?.format
      return this.currentCart?.attributes?.total_price?.format
    },
    displayCartSize() {
      if (this.v2Cart) return this.v2CartQuantity
      return this.$store.getters.cartSize
    },
    facebookPixelContainerID() {
      return this.info?.attributes?.facebook_pixel_container_id
    },
    bottleCycleDetails() {
      return this.currentBottle?.attributes?.bottle_cycle_details
    },
    willAutocharge() {
      return this.bottleCycleDetails?.autocharge
    },
    hasUpsellItems() {
      if (this.v2Cart) {
        return this.v2LocalCartItems?.some((item) => item.frontend_key?.endsWith('-upsell')) || false
      }
      return this.$store.getters.cartItems.some((x) => x.attributes.frontend_key?.endsWith('-upsell'))
    }
  },
  data() {
    return { upsells: [], cartFlash: false }
  },
  async mounted() {
    this.publishHeight()
    if (!this.v2Cart) {
      this.upsells = (await this.$store.dispatch('getUpsells'))?.data || []
    }
  },
  beforeUnmount() {
    this.stopPublishingHeight()
  },
  watch: {
    async currentCart() {
      if (!this.v2Cart) {
        this.upsells = (await this.$store.dispatch('getUpsells'))?.data || []
      }
    },
    displayCartSize(newSize, oldSize) {
      if (!this.$store.getters.hasCartDrawer) return
      if (newSize > (oldSize || 0) && !this.drawerOpen) this.cartFlash = true
    }
  },
  methods: {
    publishHeight() {
      const element = this.$refs.basket
      if (!element || typeof document === 'undefined') return

      heightOwner = this
      const write = () => {
        if (heightOwner !== this) return
        const height = Math.round(element.getBoundingClientRect().height)
        document.documentElement.style.setProperty(BASKET_HEIGHT_VAR, `${height}px`)
      }
      write()

      // jsdom and older Safari have no ResizeObserver; the one-off measurement above still gives a
      // correct value for a bar whose contents never change after mount.
      if (typeof ResizeObserver === 'undefined') return
      this.heightObserver = new ResizeObserver(write)
      this.heightObserver.observe(element)
    },

    stopPublishingHeight() {
      if (this.heightObserver) {
        this.heightObserver.disconnect()
        this.heightObserver = null
      }
      if (heightOwner !== this) return
      heightOwner = null
      document.documentElement.style.removeProperty(BASKET_HEIGHT_VAR)
    },

    clickedCart() {
      this.$emit('showCart', !this.inCart)
    },
    async clickedProceed() {
      if (this.cartIsEmpty || !this.cartIsClear) return

      const trackingProperties = getCommonTrackingProperties(this)

      // Show upsell screen if upsells exist and not already in upsell
      const hasUpsells = this.v2Cart ? !!this.v2UpsellCategory : this.upsells?.length > 0
      if (hasUpsells && !this.inUpsell && !this.hasUpsellItems) {
        posthog.capture('proceed_to_checkout_clicked', trackingProperties)
        posthog.capture('presented_with_upsells', trackingProperties)
        this.$emit('showUpsell', true)
        return
      }

      // No upsells or already past upsell: proceed to checkout
      const cartSize = this.v2Cart ? this.v2CartQuantity : this.$store.getters.cartSize
      const price = this.v2Cart
        ? (this.v2Cart.total || 0) / 100
        : this.currentCart?.attributes?.total_price?.cents
          ? this.currentCart.attributes.total_price.cents / 100
          : this.$store.getters.checkout?.attributes.bottle_total?.cents / 100

      const fullTrackingProperties = {
        ...trackingProperties,
        cart_size: cartSize,
        price: price
      }

      const eventName = this.willAutocharge ? 'auto_charge_proceed_to_checkout' : 'proceed_to_checkout'

      posthog.capture(eventName, fullTrackingProperties)

      this.handleTracking()

      if (this.v2Cart) {
        try {
          await this.v2CommitCart()
        } catch (e) {
          this.$emit('commitFailed')
          return
        }
      } else {
        this.$store.dispatch('confirmCart')
      }

      this.$router.push({ name: this.link })
    },
    orderForDate(forBottle) {
      if (!forBottle) return null
      if (forBottle.attributes.is_bottle_cycle) {
        if (this.bottleCycleDetailsForBottle(forBottle)?.fulfillment_slot_day) {
          return new Date(this.bottleCycleDetailsForBottle(forBottle)?.fulfillment_slot_day + 'T12:00:00.000Z')
        } else if (this.bottleCycleDetailsForBottle(forBottle)?.possible_fulfillment_days.length > 0) {
          return new Date(this.bottleCycleDetailsForBottle(forBottle)?.possible_fulfillment_days[0] + 'T12:00:00.000Z')
        }
      } else {
        if (this.fulfillmentSlotForBottle(forBottle)) {
          return new Date(this.fulfillmentSlotForBottle(forBottle).attributes.fulfillment_date + 'T12:00:00.000Z')
        }
      }

      return null
    },
    bottleCycleDetailsForBottle(forBottle) {
      return forBottle?.attributes?.bottle_cycle_details
    },
    fulfillmentSlotForBottle(bottle) {
      return this.$store.getters.fulfillmentSlotForBottle(bottle)
    },
    handleTracking() {
      if (this.v2Cart) {
        window.bottle.hooks.v1.emit('begin_checkout', {
          currency: this.v2Cart.total_formatted?.currency_iso,
          value: (this.v2Cart.total || 0) / 100,
          items: (this.v2Cart.cart_items || []).map((x) => ({
            item_id: x.product_id,
            item_name: x.product_name,
            price: (x.total_price || 0) / 100,
            quantity: x.quantity
          }))
        })
      } else {
        window.bottle.hooks.v1.emit('begin_checkout', {
          currency: this.$store.getters.checkout?.attributes.bottle_total.currency_iso,
          value: this.$store.getters.checkout?.attributes.bottle_total.cents / 100,
          items: this.$store.getters.cartItems.map((x) => ({
            item_id: x.attributes.id,
            item_name: mapRelationship(this.$store.state, x.relationships.product.data).attributes.product_name,
            price: x.attributes.total_price.cents / 100,
            quantity: x.attributes.quantity
          }))
        })
      }
      if (this.facebookPixelContainerID) {
        window.fbq('track', 'InitiateCheckout')
      }
    }
  }
}
</script>

<style scoped>
.basket-cart-flash {
  animation: basket-cart-pulse 0.9s ease-out 2;
}
@keyframes basket-cart-pulse {
  0% {
    box-shadow: 0 0 0 0 hsl(var(--main-brand-hue), var(--main-brand-saturation-500), var(--main-brand-lightness-500));
  }
  100% {
    box-shadow: 0 0 0 12px transparent;
  }
}
</style>
